import { Button, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { AuthUserPermission } from '@cibo/core'
import { useAuth, usePermissions } from '@cibo/profile'
import { useWorkflowManagement } from '@cibo/programs'
import { ActionBar, FriendlyError } from '@cibo/ui'

import { PROGRAM_ROUTES } from '../../navigation/types/routes'
import { WorkflowActionBarProps } from './WorkflowActionTypes'
import { WorkflowCommitButton } from './WorkflowCommitButton'

export const WorkflowActionBar = ({
  workflow,
  viewingSummary,
  setViewingSummary,
  returnTo,
  onCommit,
  userIsWorkflowOwner,
}: WorkflowActionBarProps) => {
  const { t } = useTranslation('impact/features/Workflows')
  const navigate = useNavigate()
  const { can } = usePermissions()
  const [actionBarError, setActionBarError] = useState<string>()
  const { userId } = useAuth()
  const { setDeleteWorkflowModal } = useWorkflowManagement()
  const { benchmark, fieldCounts } = workflow

  const grower = workflow?.ownedBy?.givenName
    ? `${workflow.ownedBy.givenName} ${workflow.ownedBy.familyName}`
    : workflow.ownedBy.userPrimaryEmail
  const context = workflow && userId && workflow?.ownedBy?.userId !== userId ? 'grower' : 'self'

  return (
    <ActionBar>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          {/* @todo this ought to be a permission */}

          {viewingSummary && (
            <Button
              onClick={() => setViewingSummary(false)}
              variant="contained"
              data-testid="workflow-edit-details"
            >
              {t('backToEdit')}
            </Button>
          )}

          {actionBarError && <FriendlyError message={actionBarError} />}

          {context !== 'self' && <Typography>{t('onBehalf', { grower })}</Typography>}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          {viewingSummary && (
            <>
              {userIsWorkflowOwner || can({ useAny: AuthUserPermission.MANAGE_ENROLLMENTS }) ? (
                <Button
                  id="hat4"
                  variant="outlined"
                  color="error"
                  onClick={() =>
                    setDeleteWorkflowModal({
                      workflowId: workflow?.id,
                      onSuccess: () => navigate(returnTo, { replace: true }),
                      onUndo: ({ data: { id, programId } }) =>
                        navigate(
                          generatePath(PROGRAM_ROUTES.ENROLL_DETAIL, {
                            workflowId: id as string,
                            programId,
                          })
                        ),
                    })
                  }
                >
                  {t('delete')}
                </Button>
              ) : null}
              <Button variant="text" id="hat1" onClick={() => navigate(returnTo)}>
                {t('cancel')}
              </Button>
              <WorkflowCommitButton
                workflow={workflow}
                onCommit={onCommit}
                setViewingSummary={setViewingSummary}
                setActionBarError={setActionBarError}
              />

              {fieldCounts.pass < 1 && (
                <Button
                  id="hat3"
                  variant="contained"
                  onClick={() => navigate(returnTo)} // should this also prompt a workflow delete?
                  data-testid="summary_fail_next"
                >
                  {t('failNext')}
                </Button>
              )}
            </>
          )}

          {!viewingSummary && (
            <>
              <Button
                id="hat4"
                variant="outlined"
                color="error"
                onClick={() =>
                  setDeleteWorkflowModal({
                    workflowId: workflow?.id,
                    onSuccess: () => navigate(returnTo, { replace: true }),
                    onUndo: ({ data: { id, programId } }) =>
                      navigate(
                        generatePath(PROGRAM_ROUTES.ENROLL_DETAIL, {
                          workflowId: id as string,
                          programId,
                        })
                      ),
                  })
                }
              >
                {t('delete')}
              </Button>
              <Button
                id="hat5"
                variant="contained"
                onClick={() => navigate(returnTo)}
                disabled={fieldCounts.total === 0}
                data-testid="prequalify_save"
              >
                {t('saveProgress')}
              </Button>
              <Button
                id="hat6"
                variant="contained"
                disabled={
                  fieldCounts.total === 0 || (!!fieldCounts.total && !!fieldCounts.incomplete)
                }
                onClick={() => setViewingSummary(true)}
                data-testid="summary-progress-next"
              >
                {t('next', { context: benchmark })}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </ActionBar>
  )
}
